import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaskTriage, type TriageSuggestion } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { taskFormApi } from '../api';
import { useAiRunConfirm } from '../../ai/components/ai-run-confirm';

/**
 * Dải gợi ý khi đang gõ form tạo task — APM-AI-h.
 *
 * ## Nửa tất định chạy một mình
 *
 * Người dùng dừng gõ 800ms ⇒ gọi phần **không cần model**: việc giống, điểm
 * story tham chiếu. Nó luôn có, kể cả khi tổ chức chưa nhập key AI.
 *
 * ## Nửa AI chờ một cú bấm
 *
 * Đề bài (`plan-ai-features.md` mục 2) vẽ cả dải hiện ra sau mỗi lần dừng gõ.
 * Ở đây nửa AI nằm sau một nút, có chủ đích: mỗi lần dừng gõ là **một lời gọi
 * model**, và một người viết mô tả trong hai phút sẽ dừng gõ hàng chục lần. Đó
 * là một hoá đơn không ai bấm nút để tạo ra. Nút này đổi nó thành một hành động
 * người dùng chọn.
 */

const DEBOUNCE_MS = 800;
/** Ngắn hơn thì chưa đủ để so: FULLTEXT cần ít nhất một từ có nghĩa. */
const MIN_TITLE = 6;

export function TriageStrip({
  projectId,
  title,
  description,
  onApply,
}: {
  projectId: string;
  title: string;
  description: string;
  /** Người dùng bấm một gợi ý: form tự điền ô tương ứng. */
  onApply: (patch: { priority?: TriageSuggestion['priority']; assigneeId?: string }) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(title.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [title]);

  const enabled = debounced.length >= MIN_TITLE;
  const deterministic = useQuery({
    queryKey: ['task-triage', projectId, debounced],
    queryFn: () => taskFormApi.triage(projectId, { title: debounced, deterministicOnly: true }),
    enabled,
    staleTime: 60_000,
  });

  const [ai, setAi] = useState<TaskTriage | null>(null);
  const aiConfirm = useAiRunConfirm(projectId);
  const askAi = useMutation({
    mutationFn: () =>
      taskFormApi.triage(projectId, {
        title: debounced,
        ...(description.trim().length > 0 ? { description } : {}),
      }),
    onSuccess: setAi,
  });

  // Gõ tiếp thì gợi ý cũ không còn nói về thứ đang gõ nữa.
  useEffect(() => setAi(null), [debounced]);

  const duplicates = deterministic.data?.duplicates ?? [];
  const hint = deterministic.data?.storyPointsHint ?? null;
  const suggestion = ai?.ai ?? null;
  if (!enabled || (duplicates.length === 0 && hint === null && suggestion === null)) return null;

  return (
    <section className="flex flex-col gap-2 rounded-DEFAULT border border-border bg-muted/30 p-2 text-xs">
      {duplicates.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="font-medium">{t('triage.duplicates')}</span>
          <ul className="flex flex-col gap-0.5">
            {duplicates.map((item) => (
              <li key={item.taskId} className="flex items-center gap-2">
                <span className="font-mono text-muted-foreground">{item.key}</span>
                <span className="truncate">{item.title}</span>
                {item.statusCategory === 'done' ? (
                  <span className="text-muted-foreground">{t('triage.done')}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hint ? (
        <p>
          {t('triage.storyPoints', {
            value: hint.value,
            low: hint.low,
            high: hint.high,
            count: hint.sampleSize,
          })}
        </p>
      ) : null}

      {suggestion ? (
        <div className="flex flex-col gap-1">
          <p className="text-muted-foreground">{suggestion.reason}</p>
          <div className="flex flex-wrap gap-2">
            {suggestion.priority ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onApply({ priority: suggestion.priority })}
              >
                {t('triage.applyPriority', { value: t(`priority.${suggestion.priority}`) })}
              </Button>
            ) : null}
            {suggestion.assignee ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onApply({ assigneeId: suggestion.assignee!.userId })}
              >
                {t('triage.applyAssignee')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={askAi.isPending}
            onClick={() =>
              aiConfirm.confirm('triage_task', t('triage.askAi'), () => askAi.mutate())
            }
          >
            {askAi.isPending ? t('triage.asking') : t('triage.askAi')}
          </Button>
          {ai?.aiSkipped ? (
            <span className="text-muted-foreground">{t(`triage.skipped.${ai.aiSkipped}`)}</span>
          ) : null}
        </div>
      )}
    </section>
  );
}
